import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Lock, Brain, Sparkles, ArrowRight, BadgeCheck, Gauge } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import api from "../lib/api";
import { toast } from "sonner";
import PaymentModal from "../components/PaymentModal";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Disclaimer } from "../components/FAQ";

const DIM_LABELS = {
  critical_thinking: { en: "Critical Thinking", hi: "क्रिटिकल थिंकिंग", w: "25%" },
  personality_fit: { en: "Personality Fit", hi: "व्यक्तित्व फ़िट", w: "20%" },
  emotional_intelligence: { en: "Emotional Intelligence", hi: "भावनात्मक बुद्धिमत्ता", w: "20%" },
  career_alignment: { en: "Career Alignment", hi: "करियर संरेखण", w: "20%" },
  learning_readiness: { en: "Learning Readiness", hi: "सीखने की तत्परता", w: "15%" },
};

export function BharatIQScoreCard({ scores, compact = false }) {
  const { i18n } = useTranslation();
  const isHi = i18n.language?.startsWith("hi");
  const dims = scores.dimensions || {};
  return (
    <div className="rounded-xl border border-primary bg-primary text-primary-foreground p-6 sm:p-8 relative overflow-hidden" data-testid="bharatiq-score-card">
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-accent/30 blur-3xl" />
      <div className="relative grid md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-5">
          <div className="label-eyebrow text-white/60 flex items-center gap-2"><Gauge className="w-3.5 h-3.5" /> {isHi ? "आपका BharatIQ Score" : "Your BharatIQ Score"}</div>
          <div className="mt-3 flex items-end gap-2">
            <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="bharatiq-score-value"
              className="font-display text-6xl sm:text-7xl font-extrabold tracking-tight leading-none">{scores.bharatiq_score ?? "—"}</motion.span>
            <span className="text-white/60 text-lg pb-1">/1000</span>
          </div>
          <div className="mt-2 text-sm text-white/80" data-testid="bharatiq-percentile">
            {isHi ? `~${scores.bharatiq_percentile}% उपयोगकर्ताओं से बेहतर` : `Higher than ~${scores.bharatiq_percentile}% of users`}
          </div>
          <div className="mt-4 h-2 rounded-full bg-white/15 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${(scores.bharatiq_score || 0) / 10}%` }} transition={{ duration: 0.9 }} className="h-full bg-accent" />
          </div>
        </div>
        <div className={`md:col-span-7 grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-x-6 gap-y-3`}>
          {Object.entries(DIM_LABELS).map(([k, l]) => (
            <div key={k} data-testid={`dim-${k}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-white/90">{isHi ? l.hi : l.en} <span className="text-white/40 font-mono">{l.w}</span></span>
                <span className="font-mono font-bold">{Math.round(dims[k] ?? 0)}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-white/15 overflow-hidden">
                <div className="h-full bg-white/80" style={{ width: `${dims[k] ?? 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Result() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scores, setScores] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [payTier, setPayTier] = useState("basic");

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get(`/test/session/${sessionId}/scores`),
          api.get(`/payments/status/${sessionId}`),
        ]);
        setScores(s.data);
        if (p.data.basic_unlocked || p.data.premium_unlocked) navigate(`/dashboard?session=${sessionId}`);
      } catch (e) {
        toast.error("Could not load results");
      }
    })();
  }, [sessionId]); // eslint-disable-line

  if (!scores) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Brain className="w-7 h-7 text-primary animate-pulse" />
      </div>
    );
  }

  const radarData = [
    { trait: "Openness", value: scores.personality.O },
    { trait: "Conscientiousness", value: scores.personality.C },
    { trait: "Extraversion", value: scores.personality.E },
    { trait: "Agreeableness", value: scores.personality.A },
    { trait: "Stability", value: 100 - scores.personality.N },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Brain className="w-4 h-4" />
            </div>
            <span className="font-display font-extrabold">BharatIQ</span>
          </div>
          <LanguageSwitcher compact />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label-eyebrow">{t("result.step")}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2">{t("result.title")}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">{t("result.sub")}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mt-8">
          <BharatIQScoreCard scores={scores} />
        </motion.div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-xl border border-border bg-white p-6">
            <div className="label-eyebrow">{t("result.ct_label")}</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span data-testid="result-iq-score" className="font-display text-5xl font-extrabold text-primary">{scores.iq_score}</span>
              <span className="text-sm text-muted-foreground">/ 100</span>
            </div>
            <div className="mt-3 text-sm text-muted-foreground">{t("result.ct_sub", { p: scores.iq_percentile })}</div>
            <div className="mt-5 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${scores.iq_score}%` }} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="rounded-xl border border-border bg-white p-6 lg:col-span-2 relative overflow-hidden">
            <div className="label-eyebrow">{t("result.personality_label")}</div>
            <div className="mt-2 h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius={80}>
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11, fill: "#0A192F" }} />
                  <Radar name="You" dataKey="value" stroke="#0A192F" fill="#0A192F" fillOpacity={0.25} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="absolute inset-0 glass grid place-items-center" data-testid="result-locked-overlay">
              <div className="text-center max-w-xs">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary text-primary-foreground grid place-items-center"><Lock className="w-5 h-5" /></div>
                <div className="mt-4 font-display text-xl font-bold">{t("result.locked_title")}</div>
                <div className="mt-1.5 text-xs text-muted-foreground">{t("result.locked_sub")}</div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="rounded-xl border border-border bg-white p-6 lg:col-span-2 relative">
            <div className="label-eyebrow">{t("result.careers_label")}</div>
            <div className="mt-3 space-y-2">
              {scores.top_careers.slice(0, 4).map((c, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border last:border-0 py-2">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-md bg-primary/5 text-primary text-xs font-bold grid place-items-center font-mono">{i + 1}</span>
                    <span className="font-medium blur-sm select-none">●●●●●●●●●●●●●●</span>
                  </div>
                  <span className="text-xs text-muted-foreground blur-sm select-none">●●●●</span>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 glass grid place-items-center rounded-xl">
              <div className="text-center max-w-xs">
                <Lock className="w-5 h-5 mx-auto text-primary" />
                <div className="mt-3 text-sm font-medium">{t("result.careers_waiting")}</div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-primary bg-primary text-primary-foreground p-7 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent/30 blur-2xl" />
            <div className="relative">
              <div className="label-eyebrow text-white/60">{t("result.unlock_eyebrow")}</div>
              <div className="mt-2 font-display text-2xl font-bold leading-tight">{t("result.unlock_title")}</div>
              <ul className="mt-4 text-sm text-white/80 space-y-1.5">
                {t("result.unlock_pts", { returnObjects: true }).map((p, i) => (
                  <li key={i} className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-accent" /> {p}</li>
                ))}
              </ul>
              <button data-testid="result-unlock-btn" onClick={() => { setPayTier("basic"); setShowPay(true); }}
                className="mt-6 btn-accent w-full py-3 rounded-md font-semibold inline-flex items-center justify-center gap-2">
                {t("result.unlock_btn")} <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="result-blueprint-btn" onClick={() => { setPayTier("premium"); setShowPay(true); }}
                className="mt-3 w-full py-3 rounded-md font-semibold inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 transition-colors">
                <Sparkles className="w-4 h-4 text-accent" /> {t("result.blueprint_btn")}
              </button>
            </div>
          </div>
        </motion.div>

        <div className="mt-8"><Disclaimer compact /></div>
      </div>

      <PaymentModal open={showPay} onClose={() => setShowPay(false)} sessionId={sessionId} tier={payTier}
        onSuccess={() => { setShowPay(false); navigate(`/dashboard?session=${sessionId}`); }} />
    </div>
  );
}
